// ...existing code...
import React, { useState, useEffect, useMemo, useRef } from 'react';
import './App.css';
import logoMarkLight from './assets/logo-mark-light.png';
import { useAuth } from './agents/authAgent';
import LoginPage from './components/Auth/LoginPage';
import RegisterPage from './components/Auth/RegisterPage';
import DevUserSwitcher from './components/Auth/DevUserSwitcher';
import ForgotPasswordPage from './components/Auth/ForgotPasswordPage';
import ResetPasswordPage from './components/Auth/ResetPasswordPage';
import MagicLinkLanding from './components/MagicLinkLanding/MagicLinkLanding';
import HousesComponent from './components/Houses/HousesComponent';
import AdvancedSearchComponent from './components/AdvancedSearch/AdvancedSearchComponent';
import SavedSearchesComponent from './components/SavedSearches/SavedSearchesComponent';
import NotificationsComponent from './components/Notifications/NotificationsComponent';
import MarketInsightsComponent from './components/MarketInsights/MarketInsightsComponent';
import BuyerReadinessHub from './components/BuyerProfile/BuyerReadinessHub';
import FavoritesTab from './components/Favorites/FavoritesTab';
import TourRequestsTab from './components/Tours/TourRequestsTab';
import OfferManagement from './components/OfferManagement/OfferManagement';
import NegotiationWorkspace from './components/NegotiationWorkspace/NegotiationWorkspace';
import DocumentManagement from './components/DocumentManagement/DocumentManagement';
import SellerDashboard from './components/SellerDashboard/SellerDashboard';
import AgreementReturn from './components/SellerDashboard/AgreementReturn';
import DisclosureReturn from './components/SellerDashboard/DisclosureReturn';
import DocumentSignedReturn from './components/DocumentSignedReturn/DocumentSignedReturn';
import ContingencyHub from './components/ContingencyHub/ContingencyHub';
import TransactionJourney from './components/TransactionJourney/TransactionJourney';
import ClosingPage from './components/ClosingFlow/ClosingPage';
import AffordabilityCalc from './components/AffordabilityCalc/AffordabilityCalc';
import HouseDetailPage from './components/HouseDetail/HouseDetailPage';
import SellerTourCalendar from './components/Tours/SellerTourCalendar';
import PricingPage from './components/Pricing/PricingPage';
import LegalPage from './components/Legal/LegalPage';
import LegalFooterLinks from './components/Legal/LegalFooterLinks';
import { legalDocumentForPath } from './components/Legal/legalDocuments';
import CheckoutReturnPage from './components/Billing/CheckoutReturnPage';
import CheckoutCancelPage from './components/Billing/CheckoutCancelPage';
import DunningBanner from './components/Billing/DunningBanner';
import BillingSettingsPage from './components/Billing/BillingSettingsPage';
import UpgradePromptModal from './components/Billing/UpgradePromptModal';
import { PERMISSION_DENIED_EVENT } from './agents/apiAgent';
import billingAgent from './agents/billingAgent';
import AdminHub from './components/Admin/AdminHub';
import BuyerHub from './components/BuyerHub/BuyerHub';
import SellerHub from './components/SellerHub/SellerHub';
import ErrorBoundary from './components/ui/ErrorBoundary';
import OfferHistoryDrawer from './components/OfferManagement/history/OfferHistoryDrawer';
import { useOfferNotifications } from './components/OfferManagement/hooks/useOfferNotifications';
import { NotificationsPanel } from './components/OfferManagement/notifications/NotificationsDrawer';
import { checkAdminAccess } from './agents/adminAgent';
import {
  clearStashedAdminDeepLink,
  parseAdminDeepLink,
  stashAdminDeepLink,
  takeStashedAdminDeepLink,
} from './utils/adminDeepLink';
import { NAV_GROUPS, parseTab, defaultTabForRole, defaultWorkspaceForRole } from './navigation/navConfig';
import ListYourHomeStart from './components/ListYourHome/ListYourHomeStart';
import { clearPendingListing, loadPendingListing } from './utils/pendingListing';
import PublicLayout from './components/Public/PublicLayout';
import LandingPage from './components/Public/LandingPage';
import PublicListingPage from './components/Public/PublicListingPage';
import usePublicRoute from './hooks/usePublicRoute';
import { PublicSiteContext, useFetchPublicSiteEnabled } from './hooks/usePublicSiteEnabled';
import { homeIdForPath, takePublicIntent } from './navigation/publicRoutes';
import { DEFAULT_PAGE_TITLE, setPublicPageMeta } from './utils/publicSite';

const WORKSPACE_META = {
  shared: { label: 'Shared', shortLabel: 'Shared' },
  buyer: { label: 'Buyer', shortLabel: 'Buy' },
  seller: { label: 'Seller', shortLabel: 'Sell' },
};

const NAV_ITEM_HINTS = {
  journey: 'Track milestones from offer to closing.',
  houses: 'Browse available properties and open details fast.',
  search: 'Use advanced filters for price, beds, and more.',
  'seller-post': 'Create and publish a new listing.',
  'offers:buyer': 'Review and manage offers you submitted.',
  'offers:seller': 'Review incoming offers on your listings.',
  notifications: 'See updates requiring your attention.',
};

const DEFAULT_COLLAPSED_STATE = { shared: {}, buyer: {}, seller: {} };

function NavIcon({ name }) {
  const common = { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true };
  switch (name) {
    case 'journey': return <svg {...common}><path d="M5 19V5l7 4 7-4v14l-7 4-7-4z" /></svg>;
    case 'documents': return <svg {...common}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /></svg>;
    case 'closing': return <svg {...common}><path d="M20 7 9 18l-5-5" /></svg>;
    case 'notifications': return <svg {...common}><path d="M15 17h5l-1.4-1.4A2 2 0 0 1 18 14.2V11a6 6 0 0 0-12 0v3.2c0 .5-.2 1-.6 1.4L4 17h5" /><path d="M10 17a2 2 0 0 0 4 0" /></svg>;
    case 'buyerHub': return <svg {...common}><path d="M3 10 12 3l9 7" /><path d="M5 10v10h14V10" /></svg>;
    case 'houses': return <svg {...common}><path d="M3 11 12 4l9 7" /><path d="M5 10v10h14V10" /></svg>;
    case 'search': return <svg {...common}><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></svg>;
    case 'saved': return <svg {...common}><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" /></svg>;
    case 'favorites': return <svg {...common}><path d="m12 21-1.4-1.2C5.4 15.2 2 12.1 2 8.3 2 5.5 4.2 3.3 7 3.3c1.6 0 3.1.8 4 2 1-1.2 2.5-2 4-2 2.8 0 5 2.2 5 5 0 3.8-3.4 7-8.6 11.5z" /></svg>;
    case 'tours':
    case 'listingTours': return <svg {...common}><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>;
    case 'offers': return <svg {...common}><path d="M4 6h16v12H4z" /><path d="m4 8 8 5 8-5" /></svg>;
    case 'contingencies': return <svg {...common}><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>;
    case 'calculator': return <svg {...common}><rect x="5" y="2" width="14" height="20" rx="2" /><path d="M8 6h8M8 10h2M12 10h2M16 10h0M8 14h2M12 14h2M16 14h0M8 18h8" /></svg>;
    case 'profile': return <svg {...common}><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></svg>;
    case 'sellerHub': return <svg {...common}><path d="M3 21h18" /><path d="M5 21V9l7-4 7 4v12" /></svg>;
    case 'listings': return <svg {...common}><path d="M8 6h13" /><path d="M8 12h13" /><path d="M8 18h13" /><path d="M3 6h.01M3 12h.01M3 18h.01" /></svg>;
    case 'addListing': return <svg {...common}><path d="M12 5v14M5 12h14" /></svg>;
    case 'pricing': return <svg {...common}><path d="M12 2v20" /><path d="M17 5H9a3 3 0 0 0 0 6h6a3 3 0 0 1 0 6H7" /></svg>;
    case 'billing': return <svg {...common}><rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 10h20" /></svg>;
    default: return <svg {...common}><circle cx="12" cy="12" r="8" /></svg>;
  }
}

function App() {
  const { user, loading: authLoading, error: authError, devBypass, login, register, logout, switchUser, adminSwitchUser } = useAuth();

  // Check for a password reset token in the URL on initial load
  const initialResetToken = React.useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('reset_token') || null;
  }, []);

  // Stripe Checkout return paths (no react-router — path-based detection, same
  // pattern as the magic-link landing). Maps the redirect URL to an initial tab.
  const initialBillingTab = React.useMemo(() => {
    if (typeof window === 'undefined') return null;
    const path = window.location.pathname;
    if (path === '/billing/checkout/success') return 'billing-success';
    if (path === '/billing/checkout/cancel') return 'billing-cancel';
    // Stripe Customer Portal returns the user to /billing (and our mock portal
    // redirect lands here too). Map it to the billing settings tab.
    if (path === '/billing') return 'billing';
    return null;
  }, []);

  // Signed-out view, from the path: '/' landing, /pricing, /homes/:id (public
  // listing), /sell ("start your listing"), /login, /signup; any other path
  // shows sign-in and keeps its URL for the path-based routes below.
  // Also 'forgot-password' | 'reset-password', which have no path of their own.
  // The public pages are behind PUBLIC_SITE_ENABLED (GET /api/public/config,
  // false until it answers): while it is off, '/', /pricing and /homes/:id
  // show sign-in and /sell the start-listing page, as before the public site.
  const publicSiteEnabled = useFetchPublicSiteEnabled();
  const { route: publicRoute, go: goPublic, show: setAuthView } = usePublicRoute(
    initialResetToken ? 'reset-password' : null,
    publicSiteEnabled,
  );
  const authView = publicRoute.view;
  // 'list_home' when signup started on the "start your listing" page.
  const [signupIntent, setSignupIntent] = useState(null);
  // Address saved on that page, handed to the Post Listing wizard once.
  const [pendingListing, setPendingListing] = useState(null);
  // Unset until the user resolves, so the role's landing tab applies (QA F1):
  // a hard-coded 'buyer-hub' here sent every seller to the Buyer Hub.
  const [activeTab, setActiveTab] = useState(initialBillingTab || null);
  // The deal the Closing tab shows when opened from a journey (else it picks).
  const [closingTransactionId, setClosingTransactionId] = useState(null);
  const [offersView, setOffersView] = useState('offers'); // 'offers' | 'negotiations'
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedHouseId, setSelectedHouseId] = useState(null);
  const [autoOpenTourForHouse, setAutoOpenTourForHouse] = useState(null);
  const [offerPropertyId, setOfferPropertyId] = useState(null);
  const [negotiationTarget, setNegotiationTarget] = useState(null);
  // The transaction (and contingency) a notification opened the Contingency
  // Hub on; cleared by any other navigation.
  const [contingencyFocus, setContingencyFocus] = useState(null);
  const [searchCriteria, setSearchCriteria] = useState(null);
  const [sellerListings, setSellerListings] = useState([]);
  // A listing to reopen in the wizard at a given step, e.g. Review & Publish
  // after the DocuSign return: { houseId, step }. Cleared once handled.
  const [sellerResume, setSellerResume] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  // False until the admin probe has answered for the current user, so an admin
  // deep link doesn't flash "Admin access required" while it is in flight.
  const [adminChecked, setAdminChecked] = useState(false);
  // /admin/ownership-review?case=ID (the review-notification email's link):
  // { tab, caseId } handed to AdminHub once, then cleared.
  const [adminDeepLink, setAdminDeepLink] = useState(null);
  const [historyDrawerOffer, setHistoryDrawerOffer] = useState({ open: false, offerId: null });
  // Global upgrade prompt, opened by the APIAgent 403 permission-denied event.
  const [upgradePrompt, setUpgradePrompt] = useState({ open: false, permission: null });
  const [workspace, setWorkspace] = useState('buyer');
  // Billing nav entries stay hidden until the backend reports billing on.
  const [billingEnabled, setBillingEnabled] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);
  const [commandQuery, setCommandQuery] = useState('');
  const [commandIndex, setCommandIndex] = useState(0);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [collapsedByWorkspace, setCollapsedByWorkspace] = useState(DEFAULT_COLLAPSED_STATE);
  const [moreExpanded, setMoreExpanded] = useState({});
  const [showNavHint, setShowNavHint] = useState(() => {
    try {
      return window.localStorage.getItem('st6.navHintSeen') !== 'true';
    } catch {
      return true;
    }
  });
  const sidebarRef = useRef(null);
  const commandInputRef = useRef(null);
  const touchStartXRef = useRef(null);

  const notificationsState = useOfferNotifications({ userId: user?.id, pollMs: 60000 });
  const unreadCount = notificationsState.unreadCount;
  const unreadDisplay = unreadCount > 9 ? '9+' : String(unreadCount);

  const roleSet = useMemo(
    () => new Set([user?.role, ...(Array.isArray(user?.roles) ? user.roles : [])].filter(Boolean).map((value) => String(value).trim().toLowerCase())),
    [user?.role, user?.roles]
  );
  const supportsBuyer = roleSet.has('buyer') || !roleSet.has('seller');
  const supportsSeller = roleSet.has('seller');

  const workspaceOptions = useMemo(() => {
    const options = [];
    if (supportsBuyer) options.push({ key: 'buyer', ...WORKSPACE_META.buyer });
    if (supportsSeller) options.push({ key: 'seller', ...WORKSPACE_META.seller });
    return options;
  }, [supportsBuyer, supportsSeller]);

  const workspaceActiveIndex = useMemo(
    () => Math.max(0, workspaceOptions.findIndex((option) => option.key === workspace)),
    [workspaceOptions, workspace]
  );

  const renderWorkspaceSwitcher = (isMobile = false) => {
    if (!workspaceOptions.length) return null;

    return (
      <div
        className={`workspace-switcher${isMobile ? ' workspace-switcher--mobile' : ''}`}
        role="tablist"
        aria-label="Workspace"
        style={{
          '--workspace-count': workspaceOptions.length,
          '--workspace-active-index': workspaceActiveIndex,
        }}
      >
        <span className="workspace-switcher__thumb" aria-hidden="true" />
        {workspaceOptions.map((option) => (
          <button
            key={option.key}
            type="button"
            role="tab"
            aria-selected={workspace === option.key}
            className={`workspace-pill ${workspace === option.key ? 'active' : ''}`}
            onClick={() => handleWorkspaceChange(option.key)}
          >
            {isMobile ? option.shortLabel : option.label}
          </button>
        ))}
      </div>
    );
  };

  const visibleNavGroups = useMemo(() => {
    return NAV_GROUPS
      .filter((group) => group.workspace === workspace)
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => {
          if (item.requiresBilling && !billingEnabled) return false;
          const { role } = parseTab(item.key);
          if (!role) return true;
          return role === workspace;
        }),
      }))
      .filter((group) => group.items.length > 0);
  }, [workspace, billingEnabled]);

  const commandItems = useMemo(() => {
    const query = commandQuery.trim().toLowerCase();
    const items = NAV_GROUPS
      .filter((group) => {
        if (group.workspace === 'seller' && !supportsSeller) return false;
        if (group.workspace === 'buyer' && !supportsBuyer) return false;
        return true;
      })
      .flatMap((group) => group.items
        .filter((item) => {
          if (item.requiresBilling && !billingEnabled) return false;
          const { role } = parseTab(item.key);
          if (role === 'seller' && !supportsSeller) return false;
          if (role === 'buyer' && !supportsBuyer) return false;
          if (!query) return true;
          return item.label.toLowerCase().includes(query)
            || group.label.toLowerCase().includes(query)
            || item.key.toLowerCase().includes(query);
        })
        .map((item) => ({ ...item, groupLabel: group.label, workspace: group.workspace }))
      );
    return items.slice(0, 12);
  }, [commandQuery, supportsBuyer, supportsSeller, billingEnabled]);

  const navBadgeByKey = useMemo(() => ({
    notifications: unreadCount > 0 ? unreadDisplay : null,
    'offers:buyer': unreadCount > 0 ? unreadDisplay : null,
    'offers:seller': unreadCount > 0 ? unreadDisplay : null,
  }), [unreadCount, unreadDisplay]);

  const collapsedStorageKey = useMemo(
    () => `st6.navCollapsed.${user?.id || 'anon'}`,
    [user?.id]
  );

  const collapsedGroups = useMemo(
    () => collapsedByWorkspace[workspace] || {},
    [collapsedByWorkspace, workspace]
  );

  const topbarPrimaryAction = useMemo(() => {
    if (workspace === 'seller') {
      return { key: 'seller-post', label: 'Post Listing', title: 'Create a new listing' };
    }
    if (workspace === 'buyer') {
      return { key: 'houses', label: 'Browse Properties', title: 'Browse available properties' };
    }
    return { key: 'journey', label: 'Open Journey', title: 'Open transaction journey' };
  }, [workspace]);

  // Check admin access after user is authenticated and auth has resolved
  useEffect(() => {
    if (!user || authLoading) { setIsAdmin(false); setAdminChecked(false); return; }
    let cancelled = false;
    checkAdminAccess().then(ok => { if (!cancelled) { setIsAdmin(ok); setAdminChecked(true); } });
    return () => { cancelled = true; };
  }, [user, authLoading]);

  // Default landing tab driven by account role, re-applied when a different
  // user signs in. A Stripe-return billing tab wins on the first landing.
  const landedForUserRef = useRef(null);
  useEffect(() => {
    if (!user?.id) return;
    const firstLanding = landedForUserRef.current === null;
    landedForUserRef.current = user.id;
    if (firstLanding && initialBillingTab) return;
    setActiveTab(defaultTabForRole(user.role, user.roles));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Clean the Stripe-return path back to "/" after the billing tab is mounted,
  // so a refresh / back-nav doesn't re-trigger the return flow. The return page
  // already captured its query params (e.g. ?mock=1) on its first render.
  useEffect(() => {
    if (initialBillingTab) {
      window.history.replaceState({}, '', '/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    // Same rule as the landing tab, so the sidebar matches the page (QA F1).
    setWorkspace(defaultWorkspaceForRole(user.role, user.roles));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, supportsBuyer, supportsSeller]);

  // A visitor who started on the "start your listing" page (or opens /sell
  // signed in) lands in Post Listing with the saved address filled in. Runs
  // after the workspace default above so it wins.
  useEffect(() => {
    if (!user?.id) return;
    const saved = loadPendingListing();
    const cameToSell = window.location.pathname === '/sell';
    if (cameToSell) window.history.replaceState({}, '', '/');
    if (!supportsSeller) return;
    if (saved) setPendingListing(saved);
    if (saved || cameToSell) {
      setWorkspace('seller');
      setActiveTab('seller-post');
    }
  }, [user?.id, supportsSeller]);

  // Public pages a visitor was on when they signed in. /homes/:id, or a
  // tour/offer they asked for there before signing in, opens that listing in
  // the app; /pricing opens the Pricing tab; the public paths are cleaned back
  // to "/". Runs after the landing-tab and /sell effects so it wins, and
  // before the admin deep link so that wins.
  useEffect(() => {
    if (!user?.id) return;
    const path = window.location.pathname.replace(/\/+$/, '') || '/';
    const pathHomeId = homeIdForPath(path);
    const intent = takePublicIntent();
    if (pathHomeId || ['/login', '/signup', '/pricing'].includes(path)) {
      window.history.replaceState({}, '', '/');
    }
    setPublicPageMeta({ title: DEFAULT_PAGE_TITLE });
    const target = intent || (pathHomeId ? { homeId: pathHomeId, action: 'view' } : null);
    if (target) {
      if (target.action === 'offer' && supportsBuyer) {
        setWorkspace('buyer');
        setOfferPropertyId(target.homeId);
        setSelectedHouseId(null);
        setActiveTab('offers:buyer');
        return;
      }
      setWorkspace(supportsBuyer ? 'buyer' : 'seller');
      setActiveTab('houses');
      setSelectedHouseId(target.homeId);
      setAutoOpenTourForHouse(target.action === 'tour' ? target.homeId : null);
      return;
    }
    if (path === '/pricing') setActiveTab('pricing');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Moving between public pages (other than the landing page, which scrolls
  // to its own sections) starts at the top, like a page load would.
  useEffect(() => {
    if (!publicSiteEnabled || user || publicRoute.nonce === 0 || publicRoute.view === 'landing') return;
    window.scrollTo({ top: 0 });
  }, [publicSiteEnabled, user, publicRoute.view, publicRoute.homeId, publicRoute.nonce]);

  // Admin deep link (path-based, like /sell). Stash it on load so it survives
  // sign-in — including the 401 handler's hard redirect to /login for a stale
  // session — then apply it once a user is present. Declared after the
  // landing-tab effects above so it wins.
  useEffect(() => {
    const link = parseAdminDeepLink(window.location);
    if (link) stashAdminDeepLink(link);
  }, []);
  useEffect(() => {
    if (!user?.id) return;
    const fromPath = parseAdminDeepLink(window.location);
    const link = fromPath || takeStashedAdminDeepLink();
    if (!link) return;
    clearStashedAdminDeepLink();
    if (fromPath) window.history.replaceState({}, '', '/');
    setAdminDeepLink(link);
    setActiveTab('admin');
  }, [user?.id]);

  useEffect(() => {
    const onGlobalKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setCommandOpen(true);
      }
      if (event.key === 'Escape') {
        setSidebarOpen(false);
        setCommandOpen(false);
        setUserMenuOpen(false);
      }
    };
    window.addEventListener('keydown', onGlobalKeyDown);
    return () => window.removeEventListener('keydown', onGlobalKeyDown);
  }, []);

  // Global 403 gating backstop: APIAgent dispatches `billing:permission-denied`
  // when a call is rejected by the backend entitlement gate. Open the shared
  // upgrade prompt with the denied permission so the user sees the upgrade pitch
  // instead of a raw error.
  useEffect(() => {
    const onPermissionDenied = (event) => {
      const permission = event?.detail?.permission || null;
      setUpgradePrompt({ open: true, permission });
    };
    window.addEventListener(PERMISSION_DENIED_EVENT, onPermissionDenied);
    return () => window.removeEventListener(PERMISSION_DENIED_EVENT, onPermissionDenied);
  }, []);

  useEffect(() => {
    let cancelled = false;
    billingAgent.getConfig()
      .then((config) => {
        if (!cancelled) setBillingEnabled(config?.billing_enabled === true);
      })
      .catch(() => {
        // Unknown → treat billing as off; the backend gates every billing route.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!commandOpen) return;
    setCommandIndex(0);
    requestAnimationFrame(() => commandInputRef.current?.focus());
  }, [commandOpen]);

  useEffect(() => {
    if (!commandOpen) setCommandQuery('');
  }, [commandOpen]);

  useEffect(() => {
    if (!showNavHint) {
      try {
        window.localStorage.setItem('st6.navHintSeen', 'true');
      } catch {
        // no-op when storage is unavailable
      }
    }
  }, [showNavHint]);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(collapsedStorageKey);
      if (!raw) {
        setCollapsedByWorkspace(DEFAULT_COLLAPSED_STATE);
        return;
      }
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') {
        setCollapsedByWorkspace(DEFAULT_COLLAPSED_STATE);
        return;
      }
      setCollapsedByWorkspace({
        shared: parsed.shared && typeof parsed.shared === 'object' ? parsed.shared : {},
        buyer: parsed.buyer && typeof parsed.buyer === 'object' ? parsed.buyer : {},
        seller: parsed.seller && typeof parsed.seller === 'object' ? parsed.seller : {},
      });
    } catch {
      setCollapsedByWorkspace(DEFAULT_COLLAPSED_STATE);
    }
  }, [collapsedStorageKey]);

  useEffect(() => {
    try {
      window.localStorage.setItem(collapsedStorageKey, JSON.stringify(collapsedByWorkspace));
    } catch {
      // no-op when storage is unavailable
    }
  }, [collapsedByWorkspace, collapsedStorageKey]);

  useEffect(() => {
    setCommandIndex((prev) => {
      if (commandItems.length === 0) return 0;
      return Math.min(prev, commandItems.length - 1);
    });
  }, [commandItems]);

  useEffect(() => {
    const onDocumentPointerDown = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('.topbar-user-menu')) return;
      setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocumentPointerDown);
    return () => document.removeEventListener('mousedown', onDocumentPointerDown);
  }, []);

  useEffect(() => {
    if (!sidebarOpen || !window.matchMedia('(max-width: 1023px)').matches) return;

    const sidebarEl = sidebarRef.current;
    if (!sidebarEl) return;

    const focusable = sidebarEl.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    if (focusable instanceof HTMLElement) focusable.focus();

    const trap = (event) => {
      if (event.key !== 'Tab') return;
      const nodes = sidebarEl.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', trap);
    return () => document.removeEventListener('keydown', trap);
  }, [sidebarOpen]);

  // ── Magic-link landing (path-based "route") ──────────────────
  // Take precedence over BOTH the loading screen and the auth gate: the
  // landing page runs its own verify() against /api/auth/magic-link/verify
  // and bounces to "/" on success, where the normal auth flow will pick up
  // the freshly-minted session cookie via validateToken().
  const isMagicLinkLanding =
    typeof window !== 'undefined' && window.location.pathname === '/auth/magic-link';
  if (isMagicLinkLanding && !user) {
    return <MagicLinkLanding />;
  }

  // ── Privacy Notice / Terms of Service (path-based "routes") ──
  // Public for everyone, signed in or not: the sign-up disclaimer links here
  // before an account exists, and the listing agreement incorporates both.
  const legalDocument =
    typeof window !== 'undefined' ? legalDocumentForPath(window.location.pathname) : null;
  if (legalDocument) {
    return <LegalPage document={legalDocument} />;
  }

  // ── Auth loading screen ──────────────────────────────────────
  if (authLoading) {
    return (
      <div className="app-user-loading" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        Loading…
      </div>
    );
  }

  // ── Auth gate: public site, then login / register ────────────
  if (!user) {
    const goToSignUp = () => {
      // Keep the listing intent if they started on the listing page.
      setSignupIntent(loadPendingListing() ? 'list_home' : null);
      setAuthView('register');
    };
    const goToSignIn = () => setAuthView('login');
    // Links back to the public site only exist while it is on.
    const goHomePublic = publicSiteEnabled ? () => goPublic('/') : undefined;

    // The public pages come first so they work in dev bypass too (their
    // sign-in/sign-up buttons then lead to the user picker).
    // Off: these views fall through to the sign-in page below.
    if (publicSiteEnabled && (authView === 'landing' || authView === 'pricing' || authView === 'home')) {
      let page;
      if (authView === 'landing') {
        page = (
          <LandingPage
            onNavigate={goPublic}
            onSignIn={goToSignIn}
            onSignUp={goToSignUp}
            section={publicRoute.section}
            sectionNonce={publicRoute.nonce}
          />
        );
      } else if (authView === 'pricing') {
        page = (
          <PricingPage
            onBrowseHomes={goToSignUp}
            onListHome={() => goPublic('/sell')}
          />
        );
      } else {
        page = (
          <PublicListingPage
            key={publicRoute.homeId}
            homeId={publicRoute.homeId}
            onNavigate={goPublic}
            onRequireAccount={(mode) => (mode === 'register' ? goToSignUp() : goToSignIn())}
          />
        );
      }
      return (
        <PublicLayout current={authView} onNavigate={goPublic} onSignIn={goToSignIn} onSignUp={goToSignUp}>
          {page}
        </PublicLayout>
      );
    }
    if (authView === 'list-home') {
      return (
        <ListYourHomeStart
          onCreateAccount={() => {
            setSignupIntent('list_home');
            setAuthView('register');
          }}
          onSignIn={goToSignIn}
          onHome={goHomePublic}
          onJoinWaitlist={publicSiteEnabled ? () => goPublic('/#waitlist') : undefined}
        />
      );
    }
    // Dev bypass: show user picker instead of login
    if (devBypass) {
      return <DevUserSwitcher onSwitch={switchUser} error={authError} />;
    }
    if (authView === 'register') {
      return (
        <RegisterPage
          onRegister={register}
          onSwitchToLogin={goToSignIn}
          onHome={goHomePublic}
          error={authError}
          signupIntent={signupIntent}
        />
      );
    }
    if (authView === 'forgot-password') {
      return (
        <ForgotPasswordPage
          onBackToLogin={goToSignIn}
        />
      );
    }
    if (authView === 'reset-password') {
      return (
        <ResetPasswordPage
          token={initialResetToken}
          onSuccess={() => {
            // Clear the token from the URL and go to login
            window.history.replaceState({}, '', window.location.pathname);
            setAuthView('login');
          }}
        />
      );
    }
    return (
      <LoginPage
        onLogin={login}
        onSwitchToRegister={goToSignUp}
        onForgotPassword={() => setAuthView('forgot-password')}
        onStartListing={() => setAuthView('list-home')}
        onHome={goHomePublic}
        error={authError}
      />
    );
  }

  // ── Authenticated app ────────────────────────────────────────
  const userId = user.id;

  // ── DocuSign listing-agreement return (path-based "route") ───
  // DocuSign redirects the seller to /seller/listings/:houseId/agreement-return
  // after embedded signing. Reconcile requires the authenticated seller, so we
  // render this *inside* the auth gate. onDone clears the return URL and routes
  // the seller back into their listings so the wizard re-fetches agreement
  // status (signed → publish enabled; voided → "Sign again").
  const isAgreementReturn =
    typeof window !== 'undefined' &&
    /^\/seller\/listings\/\d+\/agreement-return\/?$/.test(window.location.pathname);
  if (isAgreementReturn) {
    return (
      <AgreementReturn
        onDone={() => {
          window.history.replaceState({}, '', '/');
          setWorkspace('seller');
          setActiveTab('seller-listings');
          setSelectedHouseId(null);
        }}
        onReviewAndPublish={(houseId) => {
          window.history.replaceState({}, '', '/');
          setWorkspace('seller');
          setActiveTab('seller-listings');
          setSelectedHouseId(null);
          setSellerResume({ houseId, step: 'review' });
        }}
      />
    );
  }

  // ── DocuSign condition-report return (path-based "route") ───
  // DocuSign redirects the seller to /seller/listings/:houseId/disclosure-return
  // after signing the Wisconsin RECR / VLDR. Reconcile needs the signed-in
  // seller, so this sits inside the auth gate; onDone reopens the listing at
  // its Seller Disclosure step, which re-reads the report's status.
  const isDisclosureReturn =
    typeof window !== 'undefined' &&
    /^\/seller\/listings\/\d+\/disclosure-return\/?$/.test(window.location.pathname);
  if (isDisclosureReturn) {
    return (
      <DisclosureReturn
        onDone={(houseId) => {
          window.history.replaceState({}, '', '/');
          setWorkspace('seller');
          setActiveTab('seller-listings');
          setSelectedHouseId(null);
          if (houseId != null) setSellerResume({ houseId, step: 'disclosure' });
        }}
      />
    );
  }

  // ── DocuSign purchase-agreement return (path-based "route") ───
  // DocuSign redirects a signer to /documents/:id/signed after the embedded
  // ceremony for a two-party document. The landing needs the authenticated
  // signer to fetch document status, so it renders inside the auth gate. onDone
  // clears the return URL and drops the signer back into their negotiations,
  // where DocumentFlowPanel reflects the (webhook-finalized) envelope status.
  const isDocumentSignedReturn =
    typeof window !== 'undefined' &&
    /^\/documents\/\d+\/signed\/?$/.test(window.location.pathname);
  if (isDocumentSignedReturn) {
    return (
      <DocumentSignedReturn
        currentUserId={user.id}
        onDone={() => {
          window.history.replaceState({}, '', '/');
          setActiveTab('offers');
          setOffersView('negotiations');
          setSelectedHouseId(null);
        }}
      />
    );
  }

  // The role's landing tab in its own workspace, so the sidebar matches the
  // page (QA F1: a dual-role user on Selling got the Buyer Hub).
  const goHome = () => {
    setWorkspace(defaultWorkspaceForRole(user.role, user.roles));
    setActiveTab(defaultTabForRole(user.role, user.roles));
  };

  const handleNavClick = (tab) => {
    setActiveTab(tab);
    setSelectedHouseId(null);
    setSidebarOpen(false);
    setUserMenuOpen(false);
    setCommandOpen(false);
    // Prefer the current workspace's group so shared items (journey, documents, etc.)
    // don't inadvertently switch the workspace when both groups contain the tab.
    const group = NAV_GROUPS.find((candidate) => candidate.workspace === workspace && candidate.items.some((item) => item.key === tab))
      || NAV_GROUPS.find((candidate) => candidate.items.some((item) => item.key === tab));
    if (group?.workspace && WORKSPACE_META[group.workspace]) {
      setWorkspace(group.workspace);
    }
    const { base } = parseTab(tab);
    if (showNavHint) setShowNavHint(false);
    if (base !== 'offers') setOffersView('offers');
    if (base !== 'offers') setNegotiationTarget(null);
    if (base !== 'search') setSearchCriteria(null);
    setContingencyFocus(null);
  };

  // A notification row: open its tab (already scoped to the viewer's side,
  // e.g. `contingencies:seller`) on the transaction and contingency it names.
  const handleNotificationNavigate = (tab, params = {}) => {
    handleNavClick(tab);
    const { base } = parseTab(tab);
    if (base === 'contingencies' && (params.transactionId || params.contingencyId)) {
      setContingencyFocus({
        transactionId: params.transactionId || null,
        contingencyId: params.contingencyId || null,
      });
    }
  };

  const handleWorkspaceChange = (nextWorkspace) => {
    const nextVisibleKeys = new Set(
      NAV_GROUPS
        .filter((group) => group.workspace === nextWorkspace)
        .flatMap((group) => group.items)
        .filter((item) => {
          const { role } = parseTab(item.key);
          if (!role) return true;
          return role === nextWorkspace;
        })
        .map((item) => item.key)
    );

    setWorkspace(nextWorkspace);
    if (nextVisibleKeys.has(activeTab)) return;
    const fallback = NAV_GROUPS.find((group) => group.workspace === nextWorkspace);
    if (fallback?.items?.[0]?.key) {
      handleNavClick(fallback.items[0].key);
    }
  };

  const toggleGroup = (workspaceKey) => {
    setCollapsedByWorkspace((prev) => {
      const workspaceState = prev[workspace] || {};
      return {
        ...prev,
        [workspace]: {
          ...workspaceState,
          [workspaceKey]: !workspaceState[workspaceKey],
        },
      };
    });
  };

  const handleSidebarNavKeyDown = (event) => {
    const keys = ['ArrowDown', 'ArrowUp', 'Home', 'End'];
    if (!keys.includes(event.key)) return;
    const buttons = Array.from(sidebarRef.current?.querySelectorAll('.nav-item') || []);
    if (!buttons.length) return;
    event.preventDefault();
    const current = document.activeElement;
    const index = buttons.indexOf(current);
    if (event.key === 'Home') return buttons[0].focus();
    if (event.key === 'End') return buttons[buttons.length - 1].focus();
    if (event.key === 'ArrowDown') {
      const next = index < 0 ? 0 : (index + 1) % buttons.length;
      buttons[next].focus();
      return;
    }
    const prev = index <= 0 ? buttons.length - 1 : index - 1;
    buttons[prev].focus();
  };

  const handleSidebarTouchStart = (event) => {
    touchStartXRef.current = event.touches?.[0]?.clientX ?? null;
  };

  const handleSidebarTouchEnd = (event) => {
    if (!window.matchMedia('(max-width: 1023px)').matches) return;
    const startX = touchStartXRef.current;
    const endX = event.changedTouches?.[0]?.clientX;
    if (typeof startX !== 'number' || typeof endX !== 'number') return;
    if (startX - endX > 60) setSidebarOpen(false);
  };

  const handleCommandKeyDown = (event) => {
    if (!commandOpen) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setCommandIndex((prev) => (commandItems.length ? (prev + 1) % commandItems.length : 0));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setCommandIndex((prev) => (commandItems.length ? (prev - 1 + commandItems.length) % commandItems.length : 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const target = commandItems[commandIndex];
      if (target) handleNavClick(target.key);
    }
  };

  const TopbarUser = () => (
    <div className="topbar-user-menu">
      <button
        type="button"
        className="app-header__actions__user"
        aria-haspopup="menu"
        aria-expanded={userMenuOpen}
        aria-label={`Open user menu for ${user.first_name}`}
        onClick={() => setUserMenuOpen((prev) => !prev)}
      >
        <span className="app-header__actions__user-avatar" aria-hidden="true">👤</span>
        <span className="app-header__actions__user-name">{user.first_name}</span>
        <span className="app-header__actions__user-caret" aria-hidden="true">▾</span>
      </button>
      {userMenuOpen && (
        <div className="app-user-menu-dropdown" role="menu" aria-label="User options">
          <button type="button" role="menuitem" className="app-user-menu-item" onClick={goHome}>Go to home</button>
          <button type="button" role="menuitem" className="app-user-menu-item" onClick={() => handleNavClick('notifications')}>
            <span>Notifications</span>
            {unreadCount > 0 && <span className="app-user-menu-item__badge">{unreadDisplay}</span>}
          </button>
          <button type="button" role="menuitem" className="app-user-menu-item" onClick={() => setCommandOpen(true)}>Open command bar</button>
          {devBypass && (
            <button type="button" role="menuitem" className="app-user-menu-item" onClick={() => { logout(); }}>
              Switch user
            </button>
          )}
          <button type="button" role="menuitem" className="app-user-menu-item app-user-menu-item--danger" onClick={logout}>Sign out</button>
        </div>
      )}
    </div>
  );

  const handleViewHouse = (houseId) => {
    setActiveTab('houses');
    setSelectedHouseId(houseId);
    setAutoOpenTourForHouse(null);
    setSidebarOpen(false);
  };

  const handleScheduleTour = (houseId) => {
    setActiveTab('houses');
    setSelectedHouseId(houseId);
    setAutoOpenTourForHouse(houseId);
    setSidebarOpen(false);
  };

  const handleBackToListings = () => {
    setSelectedHouseId(null);
  };

  const handleMakeOffer = (propertyId) => {
    setOfferPropertyId(propertyId);
    // Making an offer is always a buyer action — force buyer mode so a seller
    // (or dual-role user) lands in "My Offers" and not "Offers Received".
    setActiveTab('offers:buyer');
    setSelectedHouseId(null);
  };

  const handleNavigateToNegotiations = (target = null) => {
    setNegotiationTarget(target);
    setActiveTab('offers');
    setOffersView('negotiations');
    setSelectedHouseId(null);
    setSidebarOpen(false);
  };

  const renderContent = () => {
    // The landing tab is set once the user resolves; render nothing until then
    // rather than flashing the Buyer Hub at a seller.
    if (!activeTab) return null;
    const { base, role: tabRole } = parseTab(activeTab);
    switch (base) {
      case 'buyer-hub':
        return (
          <BuyerHub
            user={user}
            onNavigate={handleNavClick}
            onViewHouse={handleViewHouse}
          />
        );
      case 'seller-hub':
        return (
          <SellerHub user={user} onNavigate={handleNavClick} sharedListings={sellerListings} setSharedListings={setSellerListings} />
        );
      case 'houses':
        if (selectedHouseId) {
          return (
            <HouseDetailPage
              houseId={selectedHouseId}
              buyerId={userId}
              onBack={handleBackToListings}
              onMakeOffer={handleMakeOffer}
              autoOpenTour={autoOpenTourForHouse === selectedHouseId}
              onTourAutoOpenHandled={() => setAutoOpenTourForHouse(null)}
            />
          );
        }
        return <HousesComponent buyerId={userId} onViewHouse={handleViewHouse} initialSemanticQuery={searchCriteria?._semanticQuery} />;
      case 'search':
        return (
          <AdvancedSearchComponent
            initialFilters={searchCriteria}
            buyerId={userId}
            onViewHouse={handleViewHouse}
            onRequestTour={handleScheduleTour}
          />
        );
      case 'saved':
        return <SavedSearchesComponent userId={userId} onSelectSearch={(search) => {
          if (search.criteria?.search_type === 'semantic') {
            setSearchCriteria({ _semanticQuery: search.criteria.query });
            setActiveTab('houses');
          } else {
            setSearchCriteria(search.criteria);
            setActiveTab('search');
          }
        }} />;
      case 'notifications':
        // Deal updates (offers and contingencies; the unread badges count
        // these) above the saved-search listing alerts.
        return (
          <>
            <section className="deal-notifications" aria-labelledby="deal-notifications-heading">
              <h3 id="deal-notifications-heading">Offer and contingency updates</h3>
              <NotificationsPanel
                notifications={notificationsState}
                onNavigate={handleNotificationNavigate}
                onOpenHistory={({ offerId }) => setHistoryDrawerOffer({ open: true, offerId })}
              />
            </section>
            <NotificationsComponent userId={userId} />
          </>
        );
      case 'market':
        return <MarketInsightsComponent />;
      case 'seller':
      case 'seller-listings':
        return (
          <SellerDashboard
            sellerId={userId}
            defaultView="my-listings"
            onNavigateToNegotiations={handleNavigateToNegotiations}
            sharedListings={sellerListings}
            setSharedListings={setSellerListings}
            resumeListing={sellerResume}
            onResumeHandled={() => setSellerResume(null)}
          />
        );
      case 'seller-post':
        return (
          <SellerDashboard
            sellerId={userId}
            defaultView="post-listing"
            onNavigateToNegotiations={handleNavigateToNegotiations}
            sharedListings={sellerListings}
            setSharedListings={setSellerListings}
            initialListing={pendingListing}
            onInitialListingUsed={() => {
              clearPendingListing();
              setPendingListing(null);
            }}
          />
        );
      case 'profile':
        return <BuyerReadinessHub buyerId={userId} />;
      case 'favorites':
        return <FavoritesTab buyerId={userId} onViewHouse={handleViewHouse} />;
      case 'seller-tours':
        return <SellerTourCalendar userId={userId} />;
      case 'tours':
        return <TourRequestsTab buyerId={userId} />;
      case 'offers': {
        // tabRole: 'buyer' | 'seller' | null. Falls back to account role.
        const offersRole = tabRole || user.role;
        return (
          <>
            <div className="sub-tab-bar" role="tablist" aria-label="Offers navigation">
              <button
                role="tab"
                aria-selected={offersView === 'offers'}
                className={`sub-tab ${offersView === 'offers' ? 'active' : ''}`}
                onClick={() => setOffersView('offers')}
              >
                📄 Offers
              </button>
              <button
                role="tab"
                aria-selected={offersView === 'negotiations'}
                className={`sub-tab ${offersView === 'negotiations' ? 'active' : ''}`}
                onClick={() => setOffersView('negotiations')}
              >
                🤝 Negotiations
              </button>
            </div>
            {offersView === 'offers'
              ? <OfferManagement buyerId={userId} userRole={offersRole} role={offersRole} initialPropertyId={offerPropertyId} onViewHouse={handleViewHouse} onNavigateToNegotiations={handleNavigateToNegotiations} />
              : (
                <NegotiationWorkspace
                  buyerId={userId}
                  userRole={offersRole}
                  role={offersRole}
                  onNavigateToDocuments={() => setActiveTab('documents')}
                  initialOfferId={negotiationTarget?.offerId}
                  initialPropertyId={negotiationTarget?.propertyId}
                  initialPropertyAddress={negotiationTarget?.propertyAddress}
                  onInitialTargetHandled={() => setNegotiationTarget(null)}
                />
              )
            }
          </>
        );
      }
      case 'contingencies':
        return <ContingencyHub buyerId={userId} role={tabRole || user.role} focus={contingencyFocus} />;
      case 'documents':
        return (
          <DocumentManagement
            onNavigateToNegotiations={() => handleNavigateToNegotiations()}
            onNavigateToListings={() => setActiveTab('seller-listings')}
          />
        );
      case 'journey':
        return (
          <TransactionJourney
            buyerId={userId}
            role={tabRole || user.role}
            onNavigateToListings={() => setActiveTab('seller-listings')}
            onNavigateToOffers={() => setActiveTab('offers')}
            onNavigateToContingencies={() => setActiveTab('contingencies')}
            onNavigateToDocuments={() => setActiveTab('documents')}
            onNavigateToClosing={(transactionId) => {
              setClosingTransactionId(transactionId || null);
              setActiveTab('closing');
            }}
            onScheduleTour={handleScheduleTour}
            onViewHouse={handleViewHouse}
          />
        );
      case 'closing':
        return (
          <ClosingPage
            userId={userId}
            role={tabRole || user.role}
            transactionId={closingTransactionId}
          />
        );
      case 'affordability':
        return <AffordabilityCalc />;
      case 'pricing':
        return (
          <PricingPage
            onBrowseHomes={() => handleNavClick('houses')}
            onListHome={() => handleNavClick('seller-post')}
          />
        );
      case 'billing':
        return <BillingSettingsPage user={user} />;
      case 'billing-success':
        return (
          <CheckoutReturnPage
            onContinue={goHome}
            onViewBilling={() => handleNavClick('billing')}
          />
        );
      case 'billing-cancel':
        return <CheckoutCancelPage onSeePricing={() => setActiveTab('pricing')} />;
      case 'admin':
        if (!isAdmin && !adminChecked) {
          return (
            <div style={{ padding: '2rem', textAlign: 'center' }} role="status">
              <p>Checking admin access…</p>
            </div>
          );
        }
        if (!isAdmin) {
          return (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <h2>Admin access required</h2>
              <p>You don't have permission to view this page.</p>
            </div>
          );
        }
        return (
          <AdminHub
            onSwitchUser={adminSwitchUser}
            initialTab={adminDeepLink?.tab || null}
            initialOwnershipCaseId={adminDeepLink?.caseId ?? null}
            onDeepLinkHandled={() => setAdminDeepLink(null)}
          />
        );
      default:
        return <BuyerHub user={user} onNavigate={handleNavClick} onViewHouse={handleViewHouse} />;
    }
  };

  return (
    <div className="app-layout">
      {/* Mobile topbar */}
      <div className="app-topbar">
        <div className="app-topbar-left">
          <button
            className="hamburger-button"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label="Toggle navigation menu"
            aria-expanded={sidebarOpen}
            aria-controls="main-sidebar"
          >
            ☰
          </button>
          <img src={logoMarkLight} alt="cutthe6.com" className="app-topbar-logo" />
          <span className="app-topbar-brand">cutthe6.com</span>
        </div>
        {renderWorkspaceSwitcher(true)}
        {user && (
          <div className="app-topbar-actions">
            <button
              type="button"
              className="topbar-primary-cta"
              onClick={() => handleNavClick(topbarPrimaryAction.key)}
              title={topbarPrimaryAction.title}
            >
              {topbarPrimaryAction.label}
            </button>
            {isAdmin && (
              <button
                type="button"
                className={`topbar-admin-btn ${activeTab === 'admin' ? 'active' : ''}`}
                onClick={() => handleNavClick('admin')}
                aria-current={activeTab === 'admin' ? 'page' : undefined}
                title="Admin"
              >
                <span aria-hidden="true">⚙</span>
                <span className="topbar-admin-btn__label">Admin</span>
              </button>
            )}
            <TopbarUser />
          </div>
        )}
      </div>
      {/*Desktop topbar with notifications*/}
      <div className="app-topbar-desktop">
        <div className="app-topbar-desktop__left">
          <img src={logoMarkLight} alt="" aria-hidden="true" className="app-topbar-logo" />
          <span className="app-topbar-brand">cutthe6.com</span>
          {renderWorkspaceSwitcher()}
        </div>
        {user && (
          <div className="app-header__actions app-header__actions--topbar">
            <button
              type="button"
              className="topbar-primary-cta"
              onClick={() => handleNavClick(topbarPrimaryAction.key)}
              title={topbarPrimaryAction.title}
            >
              {topbarPrimaryAction.label}
            </button>
            {isAdmin && (
              <button
                type="button"
                className={`topbar-admin-btn ${activeTab === 'admin' ? 'active' : ''}`}
                onClick={() => handleNavClick('admin')}
                aria-current={activeTab === 'admin' ? 'page' : undefined}
                title="Admin"
              >
                <span aria-hidden="true">⚙</span>
                <span className="topbar-admin-btn__label">Admin</span>
              </button>
            )}
            <TopbarUser />
          </div>
        )}
      </div>

      {/* Backdrop for mobile */}
      {sidebarOpen && (
        <div
          className="sidebar-backdrop"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <nav
        id="main-sidebar"
        ref={sidebarRef}
        className={`app-sidebar ${sidebarOpen ? 'open' : ''}`}
        role="navigation"
        aria-label="Main navigation"
        onKeyDown={handleSidebarNavKeyDown}
        onTouchStart={handleSidebarTouchStart}
        onTouchEnd={handleSidebarTouchEnd}
      >
        <div className="sidebar-header">
          <div className="sidebar-header__row">
            <span className="sidebar-title">Navigation</span>
          </div>
          {user && (
            <div className="sidebar-user">
              <span className="sidebar-user-avatar">👤</span>
              <span className="sidebar-user-name">{user.first_name}</span>
            </div>
          )}
        </div>

        <div className="sidebar-nav">
          {showNavHint && (
            <aside className="sidebar-tip" role="note" aria-label="Navigation tip">
              <p><strong>Tip:</strong> Press <kbd>Ctrl</kbd> + <kbd>K</kbd> to jump anywhere.</p>
              <button
                type="button"
                className="sidebar-tip__dismiss"
                onClick={() => setShowNavHint(false)}
              >
                Got it
              </button>
            </aside>
          )}
          {visibleNavGroups.map((group) => (
            <div className="nav-group" key={group.label}>
              {(() => {
                const isGroupCollapsed = Boolean(collapsedGroups[group.workspace]);
                const primaryItems = group.items.filter((item) => !item.secondary);
                const secondaryItems = group.items.filter((item) => item.secondary);
                const activeInSecondary = secondaryItems.some((item) => item.key === activeTab);
                const isMoreOpen = activeInSecondary || Boolean(moreExpanded[group.workspace]);
                const secondaryBadgeCount = secondaryItems.reduce(
                  (count, item) => count + (navBadgeByKey[item.key] ? 1 : 0),
                  0
                );
                const renderNavItem = (item, hidden) => (
                  <button
                    key={item.key}
                    className={`nav-item ${activeTab === item.key ? 'active' : ''}`}
                    onClick={() => handleNavClick(item.key)}
                    aria-current={activeTab === item.key ? 'page' : undefined}
                    title={NAV_ITEM_HINTS[item.key] || item.label}
                    tabIndex={hidden ? -1 : undefined}
                  >
                    <span className="nav-item-icon" aria-hidden="true">
                      <NavIcon name={item.icon} />
                    </span>
                    <span className="nav-item-label">{item.label}</span>
                    {navBadgeByKey[item.key] && (
                      <span className="nav-badge" aria-label={`${navBadgeByKey[item.key]} unread`}>
                        {navBadgeByKey[item.key]}
                      </span>
                    )}
                  </button>
                );
                return (
                  <>
              <button
                type="button"
                className="nav-group-label nav-group-label--button"
                onClick={() => toggleGroup(group.workspace)}
                aria-expanded={!isGroupCollapsed}
                aria-controls={`nav-group-${group.workspace}`}
              >
                <span>{group.label}</span>
                <span className={`nav-group-caret ${isGroupCollapsed ? 'collapsed' : ''}`} aria-hidden="true">▾</span>
              </button>
              <div
                id={`nav-group-${group.workspace}`}
                className={`nav-group-content ${isGroupCollapsed ? 'is-collapsed' : ''}`}
                aria-hidden={isGroupCollapsed}
              >
                  {primaryItems.map((item) => renderNavItem(item, isGroupCollapsed))}
                  {secondaryItems.length > 0 && (
                    <>
                      <button
                        type="button"
                        className={`nav-item nav-item--more ${isMoreOpen ? 'is-open' : ''}`}
                        onClick={() => setMoreExpanded((prev) => ({
                          ...prev,
                          [group.workspace]: !isMoreOpen,
                        }))}
                        aria-expanded={isMoreOpen}
                        aria-controls={`nav-more-${group.workspace}`}
                        tabIndex={isGroupCollapsed ? -1 : undefined}
                        title="Show more navigation"
                      >
                        <span className="nav-item-icon" aria-hidden="true">
                          <svg width="16" height="16" viewBox="0 0 16 16" focusable="false">
                            <circle cx="3" cy="8" r="1.4" fill="currentColor" />
                            <circle cx="8" cy="8" r="1.4" fill="currentColor" />
                            <circle cx="13" cy="8" r="1.4" fill="currentColor" />
                          </svg>
                        </span>
                        <span className="nav-item-label">More</span>
                        {!isMoreOpen && secondaryBadgeCount > 0 && (
                          <span className="nav-badge" aria-label={`${secondaryBadgeCount} items with updates`}>
                            {secondaryBadgeCount}
                          </span>
                        )}
                        <span className={`nav-item-caret ${isMoreOpen ? '' : 'collapsed'}`} aria-hidden="true">▾</span>
                      </button>
                      <div
                        id={`nav-more-${group.workspace}`}
                        className={`nav-more-content ${isMoreOpen ? 'is-open' : ''}`}
                        aria-hidden={!isMoreOpen}
                      >
                        {secondaryItems.map((item) => renderNavItem(item, !isMoreOpen || isGroupCollapsed))}
                      </div>
                    </>
                  )}
                </div>
                  </>
                );
              })()}
            </div>
          ))}
        </div>

        <div className="sidebar-footer">
          © 2026 cutthe6.com
          <LegalFooterLinks variant="dark" className="sidebar-footer-legal" />
        </div>
      </nav>

      {/* Main content */}
      <main className="app-content">
        {/* Global past-due banner — shows on every authenticated page when the
            subscription is past_due; reads status via useSubscription (60s poll). */}
        <DunningBanner onUpdatePayment={() => handleNavClick('billing')} />
        <div className="app-content-inner">
          {/* A crash in one page must not unmount the sidebar/top bar and blank
              the app. Navigating to another tab (or house) clears a tripped
              boundary; "Try again" remounts the page. */}
          <ErrorBoundary
            name="main-content"
            variant="page"
            message="Couldn't load this page."
            resetKeys={[activeTab, selectedHouseId]}
          >
            {/* Read by the "Share" button in My Listings (public /homes/:id pages). */}
            <PublicSiteContext.Provider value={publicSiteEnabled}>
              {renderContent()}
            </PublicSiteContext.Provider>
          </ErrorBoundary>
        </div>
      </main>

      {/* Global Offer History drawer (opened from notification click-through) */}
      <OfferHistoryDrawer
        open={historyDrawerOffer.open}
        offerId={historyDrawerOffer.offerId}
        onClose={() => setHistoryDrawerOffer({ open: false, offerId: null })}
      />

      {/* Global upgrade prompt — opened by the APIAgent 403 gating backstop. */}
      <UpgradePromptModal
        open={upgradePrompt.open}
        requiredPermission={upgradePrompt.permission}
        onClose={() => setUpgradePrompt({ open: false, permission: null })}
        onUpgrade={() => {
          setUpgradePrompt({ open: false, permission: null });
          handleNavClick('pricing');
        }}
      />

      {commandOpen && (
        <div className="command-palette-overlay" role="presentation" onClick={() => setCommandOpen(false)}>
          <div className="command-palette" role="dialog" aria-modal="true" aria-label="Command bar" onClick={(event) => event.stopPropagation()}>
            <input
              ref={commandInputRef}
              type="text"
              className="command-palette__input"
              placeholder="Jump to a page…"
              value={commandQuery}
              onChange={(event) => setCommandQuery(event.target.value)}
              onKeyDown={handleCommandKeyDown}
            />
            <div className="command-palette__list" role="listbox" aria-label="Available pages">
              {commandItems.length === 0 && (
                <div className="command-palette__empty">No matches found.</div>
              )}
              {commandItems.map((item, index) => (
                <button
                  key={item.key}
                  type="button"
                  role="option"
                  aria-selected={index === commandIndex}
                  className={`command-palette__item ${index === commandIndex ? 'active' : ''}`}
                  onMouseEnter={() => setCommandIndex(index)}
                  onClick={() => handleNavClick(item.key)}
                >
                  <span className="command-palette__icon"><NavIcon name={item.icon} /></span>
                  <span className="command-palette__text">{item.label}</span>
                  <span className="command-palette__meta">{item.groupLabel}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
